import { Controller } from "@hotwired/stimulus";
import { connectPreview } from "../base_path";

// "Ask a question" modal (SUR-2302). Scoped to the layout's .ss-c-wrap so any
// button on the page can open it via data-action="connect-contact#open"; the
// React islands open it by dispatching a `connect:contact-open` CustomEvent on
// window (caught by the data-action on the controller root).
//
// On open we probe GET /account/session once: a signed-in Connect registrant
// gets the contact fields hidden ("Asking as <name>") — the server resolves
// their Person from the session, so only the message is required. Anonymous
// visitors fill name/email like any lead form.
//
// Submission POSTs to /_lead via fetch (Accept: application/json) and swaps in
// the thank-you; a transport failure falls back to a native form post (the
// server redirects back with ?rsvp=success), mirroring lead_capture_controller.
export default class extends Controller {
  static targets = [
    "modal", "form", "contactFields", "signedInNote", "signedInName",
    "email", "phone", "message", "error", "success", "intro",
    "registrationPrompt",
  ];
  static values = {
    sessionUrl: { type: String, default: "/account/session" },
    endpoint: { type: String, default: "/_lead" },
    requiresRegistration: { type: Boolean, default: false },
  };

  connect() {
    this.boundKeydown = (e) => {
      if (e.key === "Escape") this.close();
    };
    if (this.requiresRegistrationValue && window.location.hash === "#schedule-consultation") {
      setTimeout(() => this.open(), 0);
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown);
  }

  open(event) {
    // The no-JS fallback on trigger links is a mailto: — with JS present we
    // intercept and capture instead.
    if (event) event.preventDefault();
    this.modalTarget.hidden = false;
    document.addEventListener("keydown", this.boundKeydown);
    if (this.requiresRegistrationValue) {
      this.formTarget.hidden = true;
      if (this.hasRegistrationPromptTarget) this.registrationPromptTarget.hidden = true;
      if (window.location.hash !== "#schedule-consultation") {
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#schedule-consultation`);
      }
    }
    this.probeSession();
    if (!this.requiresRegistrationValue && this.hasMessageTarget) this.messageTarget.focus();
  }

  close() {
    this.modalTarget.hidden = true;
    document.removeEventListener("keydown", this.boundKeydown);
    if (this.requiresRegistrationValue && window.location.hash === "#schedule-consultation") {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    }
  }

  // Close on backdrop click (the modal element itself, not its card).
  backdrop(event) {
    if (event.target === this.modalTarget) this.close();
  }

  async probeSession() {
    // Cache only a POSITIVE result: a visitor can register mid-page-load (the
    // AVM gate) and then open this modal — a cached signed-out answer would
    // wrongly show the contact fields again (found in live QA). Signed-in
    // can't revert without a reload, so that side is safe to cache.
    if (this.signedIn) {
      this.showContactForm();
      return;
    }
    // The editor preview renders this modal on the CRM host, where
    // /account/session lives behind the Connect subdomain constraint and 404s
    // (SUR-2548). Skip the probe — not the rest of the method: an agent
    // previewing their page should see exactly what an anonymous visitor sees,
    // which is the branch below. Returning early here would instead leave a
    // registration-gated modal with every panel hidden.
    if (!connectPreview()) {
      try {
        const res = await fetch(this.sessionUrlValue, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        });
        const data = res.ok ? await res.json() : {};
        if (data.signed_in) {
          this.contactFieldsTarget.hidden = true;
          this.signedInNoteTarget.hidden = false;
          // The session endpoint's first_name falls back to the literal "there"
          // (it feeds the header's "Hi there") — that reads as a name here
          // ("Asking as there"), so swap in second-person copy instead.
          const name = data.first_name && data.first_name !== "there" ? data.first_name : null;
          this.signedInNoteTarget.innerHTML = "";
          this.signedInNoteTarget.append(name ? `Asking as ` : "You're signed in — just type your question.");
          if (name) {
            const strong = document.createElement("strong");
            strong.style.color = "var(--ss-connect-text)";
            strong.textContent = name;
            this.signedInNoteTarget.append(strong);
          }
          this.signedIn = true;
          this.showContactForm();
          return;
        }
      } catch (_e) {
        // Anonymous path still works — leave the contact fields visible.
      }
    }

    if (this.requiresRegistrationValue && this.hasRegistrationPromptTarget) {
      this.registrationPromptTarget.hidden = false;
      if (this.hasIntroTarget) this.introTarget.hidden = true;
    } else {
      this.showContactForm();
    }
  }

  showContactForm() {
    if (this.hasRegistrationPromptTarget) this.registrationPromptTarget.hidden = true;
    if (this.hasIntroTarget) this.introTarget.hidden = false;
    this.formTarget.hidden = false;
    if (this.hasMessageTarget) this.messageTarget.focus();
  }

  async submit(event) {
    event.preventDefault();
    this.clearError();

    const message = this.messageTarget.value.trim();
    if (!message) {
      this.showError("Please enter your question.");
      this.messageTarget.focus();
      return;
    }
    const email = this.hasEmailTarget ? this.emailTarget.value.trim() : "";
    if (!this.signedIn && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.showError("Please enter a valid email address.");
      this.emailTarget.focus();
      return;
    }
    const phone = this.hasPhoneTarget ? this.phoneTarget.value.trim() : "";
    if (!this.signedIn && !phone) {
      this.showError("Please enter your mobile phone number.");
      this.phoneTarget.focus();
      return;
    }

    const button = this.formTarget.querySelector("button[type=submit]");
    if (button) button.disabled = true;
    let response;
    try {
      response = await fetch(this.endpointValue, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: new FormData(this.formTarget),
        credentials: "same-origin", // carries the Connect registrant session
      });
    } catch (_e) {
      // TRANSPORT failure only (network down, blocked): fall back to a native
      // post so the question still lands via the server's no-JS branch.
      if (button) button.disabled = false;
      this.formTarget.submit();
      return;
    }

    if (response.ok) {
      this.formTarget.hidden = true;
      if (this.hasIntroTarget) this.introTarget.hidden = true;
      this.successTarget.hidden = false;
      return;
    }

    // An HTTP error is the server REJECTING this submission — never re-post it
    // natively (that would double-submit and navigate away with no feedback).
    // Surface the server's reason inline instead.
    if (button) button.disabled = false;
    const data = await response.json().catch(() => ({}));
    if (response.status === 429 || data.error === "rate_limited") {
      this.showError("Too many messages just now — please try again in a few minutes.");
    } else {
      this.showError(data.error || "We couldn't send your question. Please check your details and try again.");
    }
  }

  showError(text) {
    this.errorTarget.textContent = text;
    this.errorTarget.classList.add("is-visible");
  }

  clearError() {
    this.errorTarget.textContent = "";
    this.errorTarget.classList.remove("is-visible");
  }
}
